<template>
  <div class="express-track-container">
    <!-- 查询输入区域 -->
    <div class="search-section">
      <div class="input-group">
        <el-input
            v-model="trackingNumber"
            placeholder="请输入快递单号"
            clearable
            @keyup.enter="handleSearch"
            @input="handleTrackingNumberChange"
            class="track-input"
        />

        <!-- 手机号输入框 -->
        <el-input
            v-model="phoneNumber"
            placeholder="手机号后四位（顺丰必填）"
            clearable
            maxlength="4"
            @keyup.enter="handleSearch"
            class="phone-input"
            :class="{ 'required-field': isSfExpress }"
        >
          <template #prepend>
            <el-icon><Phone /></el-icon>
          </template>
        </el-input>

        <el-button
            type="primary"
            @click="handleSearch"
            :loading="loading"
            class="search-btn"
        >
          <template #icon>
            <el-icon><Search /></el-icon>
          </template>
          查询
        </el-button>
      </div>

      <!-- 顺丰提示 -->
      <div v-if="isSfExpress" class="sf-tip">
        <el-alert
            title="顺丰快递查询需要输入收件人或寄件人手机号后四位"
            type="info"
            :closable="false"
            show-icon
        />
      </div>
    </div>

    <!-- 结果展示区域 -->
    <div v-if="resultData" class="result-section">
      <el-card shadow="never">
        <template #header>
          <div class="card-header">
            <div class="header-info">
              <span class="tracking-number">快递单号：{{ resultData.trackingNumber }}</span>
              <span v-if="resultData.company" class="company-name">{{ resultData.company }}</span>
            </div>
            <el-tag :type="statusTagType" size="large">
              {{ resultData.currentStatus || '运输中' }}
            </el-tag>
          </div>
        </template>

        <div v-if="resultData.records && resultData.records.length > 0">
          <el-timeline>
            <el-timeline-item
                v-for="(record, index) in resultData.records"
                :key="index"
                :timestamp="record.time"
                placement="top"
                :hollow="index !== 0"
                :type="index === 0 ? 'primary' : 'info'"
            >
              <el-card shadow="never" :class="{ 'latest-record': index === 0 }">
                <p class="record-context">{{ record.context }}</p>
                <div class="record-detail">
                  <span v-if="record.location" class="location">
                    <el-icon><Location /></el-icon>
                    {{ record.location }}
                  </span>
                  <span v-if="record.operator" class="operator">
                    <el-icon><User /></el-icon>
                    {{ record.operator }}
                  </span>
                </div>
              </el-card>
            </el-timeline-item>
          </el-timeline>
        </div>

        <div v-else class="no-records">
          <el-empty description="暂无物流记录" />
        </div>
      </el-card>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { Search, Location, User, Phone } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import axios from "axios"
import { useRoute } from 'vue-router'

const route = useRoute()
const loading = ref(false)
const trackingNumber = ref('')
const phoneNumber = ref('')
const resultData = ref(null)


// 判断是否为顺丰快递
const isSfExpress = computed(() => {
  const num = trackingNumber.value.trim().toUpperCase()
  return /^SF\d{12}$/.test(num)
})


// 状态标签样式
const statusTagType = computed(() => {
  const status = resultData.value?.currentStatus || ''
  if (status.includes('已签收')) return 'success'
  if (status.includes('派送中') || status.includes('已揽收')) return 'warning'
  if (status.includes('疑难') || status.includes('退')) return 'danger'
  return 'primary'
})

// 单号输入变化处理
const handleTrackingNumberChange = () => {
  // 清除之前的查询结果
  if (resultData.value) {
    resultData.value = null
  }

  // 如果不是顺丰快递，清空手机号
  if (!isSfExpress.value) {
    phoneNumber.value = ''
  }
}

// 查询方法
const handleSearch = async () => {
  if (!trackingNumber.value.trim()) {
    ElMessage.warning('请输入快递单号')
    return
  }

  // 顺丰快递验证手机号
  if (isSfExpress.value) {
    if (!phoneNumber.value.trim()) {
      ElMessage.warning('顺丰快递查询需要输入手机号后四位')
      return
    }

    if (!/^\d{4}$/.test(phoneNumber.value.trim())) {
      ElMessage.warning('请输入正确的手机号后四位（4位数字）')
      return
    }
  }

  try {
    loading.value = true


    // 构建请求参数
    const params = {
      trackingNumber: trackingNumber.value.trim()
    }

    // 如果是顺丰或者用户输入了手机号，添加手机号参数
    if (phoneNumber.value.trim()) {
      params.phoneNumber = phoneNumber.value.trim()
    }

    const { data } = await axios.get('/api/v1/express/query', {
      params,
      headers: { "token": localStorage.getItem('token') }
    })

    if (data.code === '0000') {
      resultData.value = {
        trackingNumber: data.data.trackingNumber,
        currentStatus: data.data.currentStatus || '运输中',
        records: data.data.records || [],
        company: getCompanyName(trackingNumber.value) // 添加快递公司显示
      }
      ElMessage.success('查询成功')
    } else {
      ElMessage.error(data.info || '查询失败')
    }
  } catch (error) {
    let errorMsg = '请求失败，请检查网络连接'

    if (error.response?.data?.info) {
      errorMsg = error.response.data.info
    } else if (error.message) {
      errorMsg = error.message
    }

    // 特殊处理顺丰手机号错误
    if (errorMsg.includes('手机号') || errorMsg.includes('701')) {
      errorMsg = '手机号不正确，请输入正确的收件人或寄件人手机号后四位'
    }

    ElMessage.error(errorMsg)
    console.error('[物流查询错误]', error)
  } finally {
    loading.value = false
  }
}

// 获取快递公司名称（简单识别）
const getCompanyName = (trackingNum) => {
  const num = trackingNum.trim().toUpperCase()

  if (/^SF\d{12}$/.test(num)) return '顺丰速运'
  if (/^1\d{12}$/.test(num)) return '申通快递'
  if (/^2\d{12}$/.test(num)) return '中通快递'
  if (/^3\d{12}$/.test(num)) return '圆通速递'
  if (/^4\d{12}$/.test(num)) return '韵达速递'
  if (/^JD\d{13,15}$/.test(num)) return '京东物流'
  if (/^YT\d{13}$/.test(num)) return '圆通速递'

  return ''
}

// 在组件挂载时从路由获取参数
onMounted(() => {
  // 从路由查询参数获取物流单号和手机号
  const routeTrackingNumber = route.query.trackingNumber
  const routePhoneNumber = route.query.phoneNumber
  
  if (routeTrackingNumber) {
    trackingNumber.value = routeTrackingNumber.toString()
    
    if (routePhoneNumber) {
      phoneNumber.value = routePhoneNumber.toString()
      
      // 延迟一点执行查询，确保组件完全渲染
      setTimeout(() => {
        handleSearch()
      }, 300)
    }
  }
})
</script>

<style scoped>
.express-track-container {
  max-width: 900px;
  margin: 20px auto;
  padding: 20px;
}

.search-section {
  margin-bottom: 30px;
}

.input-group {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}

.track-input {
  flex: 1;
  min-width: 300px;
}

.phone-input {
  width: 200px;
  transition: all 0.3s ease;
}

.phone-input.required-field {
  border-color: #f56c6c;
}

.phone-input.required-field :deep(.el-input__wrapper) {
  box-shadow: 0 0 0 1px #f56c6c inset;
}

.search-btn {
  min-width: 80px;
}

.sf-tip {
  margin-top: 12px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.tracking-number {
  font-size: 16px;
  font-weight: 500;
  color: #333;
}

.company-name {
  font-size: 12px;
  color: #666;
}

.latest-record {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  border-left: 4px solid #409eff;
}

.record-context {
  margin: 0;
  font-size: 14px;
  color: #333;
  line-height: 1.5;
}

.record-detail {
  margin-top: 8px;
  display: flex;
  gap: 20px;
  color: #666;
  font-size: 12px;
}

.location, .operator {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.no-records {
  text-align: center;
  padding: 40px 20px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .input-group {
    flex-direction: column;
    align-items: stretch;
  }

  .track-input {
    min-width: auto;
  }

  .phone-input {
    width: 100%;
  }

  .search-btn {
    width: 100%;
  }

  .card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
}
</style>